import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";

const EmailValidationCheckerContent = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container maxWidth="md" sx={{ textAlign: "center", mt: 4 }}>
      {/* Headline */}
      <Typography
        variant="h4"
        fontWeight="bold"
        sx={{ fontSize: "1.75rem" }}
        gutterBottom
      >
        Verify Any Email Instantly{" "}
        <EmailIcon color="primary" sx={{ verticalAlign: "middle" }} />
      </Typography>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Check if an Email Address is Valid, Deliverable & Safe
      </Typography>

      {/* Problem & Solution */}
      <Typography variant="body1" paragraph>
        Sending emails to invalid addresses can lead to bounce-backs and harm
        your sender reputation. Manual checks aren't reliable or scalable.
      </Typography>
      <Typography
        variant="body1"
        paragraph
        sx={{ fontWeight: "bold", color: "primary.main" }}
      >
        Our Email Validation Checker uses deep email validation to instantly
        verify an email's syntax, MX, SMTP, disposable status, and more.
      </Typography>

      {/* Benefits */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={3}>
        Why Use Our Email Validation Checker?
      </Typography>
      <ul
        style={{
          textAlign: "left",
          display: "inline-block",
          listStyleType: "none",
          paddingLeft: 0,
        }}
      >
        <li>
          ✅ <strong>Instant Validation:</strong> Get results in real-time
        </li>
        <li>
          🔍 <strong>Deep Checks:</strong> Format, MX, SMTP, Disposable, Role
          Accounts
        </li>
        <li>
          🚫 <strong>Reduce Bounces:</strong> Avoid invalid or risky emails
        </li>
        <li>
          ⚡ <strong>One-Click:</strong> Simply enter an email & validate
        </li>
        <li>
          🔐 <strong>Private & Secure:</strong> We never store your data
        </li>
      </ul>

      {/* How It Works */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        How It Works
      </Typography>
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          p: 3,
          borderRadius: 2,
          textAlign: "left",
          display: "inline-block",
        }}
      >
        <ol>
          <li>
            <strong>Enter</strong> the email address you want to validate
          </li>
          <li>
            <strong>Click</strong> the “Validate Email” button
          </li>
          <li>
            <strong>We Check</strong> the email against multiple validation
            layers
          </li>
          <li>
            <strong>View</strong> the result instantly on screen
          </li>
        </ol>
      </Box>

      {/* Social Proof */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        Trusted by Founders, Marketers & Developers
      </Typography>
      <Box sx={{ fontStyle: "italic", mb: 3 }}>
        <Typography paragraph>
          "We use this tool every day to validate user emails before onboarding
          – saved us time and headaches."
        </Typography>
        <Typography>- Ravi M., SaaS Founder</Typography>
      </Box>

      {/* FAQ Section */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Frequently Asked Questions
      </Typography>

      <Box textAlign="left" sx={{ display: "inline-block" }}>
        <Typography variant="body1" fontWeight="bold">
          📧 How accurate is the validation?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          We use multiple checks including syntax, MX, SMTP handshake, and
          blacklist status for high accuracy.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          ⏱️ How long does validation take?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Usually less than 5 seconds per email.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          🔐 Do you store the emails?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          No – all checks are done in real-time, and no email is saved on our
          servers.
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          🆓 Is the Email Validation Checker free?
        </Typography>
        <Typography variant="body2" paragraph>
          For Free plan users, 1 credit will be deducted for each validation.
          All Paid plan users enjoy this tool for free 🎁 without any credit
          deduction.
        </Typography>
      </Box>

      {/* CTA */}
      <Box mt={4} mb={6}>
        <Typography variant="h6" paragraph sx={{ fontWeight: "bold" }}>
          Ready to verify an email?
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={scrollToTop}
          sx={{
            fontSize: "1.1rem",
            py: 1.5,
            px: 4,
            fontWeight: "bold",
          }}
        >
          Validate Email →
        </Button>
        <Typography variant="caption" display="block" mt={1}>
          1 credit per validation for Free user. This is complimentary tool Paid user so 0 credits.
        </Typography>
      </Box>
    </Container>
  );
};

export default EmailValidationCheckerContent;
